import { Outlet, useLocation } from "react-router-dom";
import { Provider } from "react-redux";
import store from "./redux/store";
import { StaffModeProvider } from "./hooks/StaffModeProvider";
import { ModeProvider } from "./hooks/useMode";
import AuthChecker from "./components/Auth/AuthChecker";
import AnimationLogo from "./components/Layout/LoadingAnimation/AnimationLogo";
import LoadingBlocker from "./components/Layout/LoadingAnimation/LoadingBlocker";
import { useEffect, useContext } from "react";
import usePdfRedirect from "./hooks/usePdfRedirect";
import useTrailingSlashRedirect from "./hooks/useTrailingSlashRedirect";
import Maintenance from "./components/Maintenance/Maintenance";
import { Dialog } from "./components/Dialog/Dialog";
import TwoFactorAuthChecker from "./components/Auth/TwoFactorAuthChecker";
import GeneralLayout from "./components/Layout/GeneralLayout/GeneralLayout";
import AuthContext from "./store/AuthContext";
import EventBus, { EventBusValues } from "./utils/EventBus";
import { CustomWhatsNewDialog } from "./components/Dialog/CustomDialogs/CustomWhatsNewDialog";
import { usePendingSocialAccountLinking } from "./hooks/usePendingSocialAccountLinking";

import { resetGlobeLoader } from "./redux/globalLoaderController";
import { setPrevPathIfAllowed, getStoredPrevPath } from "./utils/logout";
import { UserPreferencesSync } from "./redux/UserPreferencesSync";
const isMaintenanceMode =
  process.env.REACT_APP_MAINTENANCE_FEATURE_FLAG === "true";

function App() {
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  usePdfRedirect();
  useTrailingSlashRedirect();
  usePendingSocialAccountLinking(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const hasSeenWhatsNewDialog = localStorage.getItem("hasSeenWhatsNewDialog");

    if (!hasSeenWhatsNewDialog) {
      EventBus.dispatch(EventBusValues.OpenDialog, {
        element: <CustomWhatsNewDialog />,
        customContent: true,
      });
      localStorage.setItem("hasSeenWhatsNewDialog", "true");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    resetGlobeLoader();
  }, [location.pathname]);

  useEffect(() => {
    const contentDiv = document.getElementById("content-container");
    if (contentDiv) {
      const eventRoutePattern = /^\/events\/([^/]+)/;
      const prevPath = getStoredPrevPath() || "";
      const matchCurrent = location.pathname.match(eventRoutePattern);
      const matchPrev = prevPath.match(eventRoutePattern);
      const currentEventId = matchCurrent?.[1];
      const prevEventId = matchPrev?.[1];
      const isSameEvent =
        currentEventId && prevEventId && currentEventId === prevEventId;

      if (isSameEvent && matchCurrent && matchPrev) {
      } else {
        contentDiv.scrollTop = 0;
        sessionStorage.removeItem("eventScrollPosition");
      }

      const currentPath = location.pathname + (location.search || "");
      setPrevPathIfAllowed(currentPath);
    }
  }, [location.pathname]);

  return isMaintenanceMode ? (
    <Maintenance />
  ) : (
    <div className="App">
      <Provider store={store}>
        <UserPreferencesSync>
        <StaffModeProvider>
          <LoadingBlocker />
          <AnimationLogo />
          <GeneralLayout>
            <AuthChecker>
              <TwoFactorAuthChecker>
                <ModeProvider>
                    <Dialog />
                    <Outlet />
                </ModeProvider>
              </TwoFactorAuthChecker>
            </AuthChecker>
          </GeneralLayout>
        </StaffModeProvider>
        </UserPreferencesSync>
      </Provider>
    </div>
  );
}

export default App;
